import {
  DropdownPrimeReactNew,
  DropdownPrimeReactNewProps,
  DropdownPrimeReactNewRef,
} from "@finanedi.es/storybook"
import { CuentaContableCuentaReadingDtoProps } from "api/DTOs/CuentaContableCuentaReadingDto"
import { CuentaContableEmpresaReadingDtoProps } from "api/DTOs/CuentaContableEmpresaReadingDto"
import ModalCrearCuentaContable from "common/components/ModalCrearCuentaContable"
import { DropdownFilterParams } from "primereact/dropdown"
import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

interface DropdownCuentasAsientosProps {
  options?: Array<
    Partial<CuentaContableEmpresaReadingDtoProps & { label: string }>
  >
  onChange?: DropdownPrimeReactNewProps["onChange"]
  className?: string
  withCreateCuenta?: boolean
  disabled?: boolean
  id: string
  loading?: boolean
  value: string | null | undefined
  listaDeCuentasBase?: CuentaContableCuentaReadingDtoProps[]
  refreshFetchData?: () => void
  cuentaPorDefecto?: string
}

const DropdownCuentasAsientos = ({
  disabled = false,
  options = [],
  onChange,
  loading = false,
  id,
  value,
  withCreateCuenta = false,
  listaDeCuentasBase = [],
  refreshFetchData,
  cuentaPorDefecto,
  className = "",
}: DropdownCuentasAsientosProps) => {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState("")

  const [isShowingCrearCuentaModal, setIsShowingCrearCuentaModal] =
    useState(false)
  const addCuentaContableTemplate = () => {
    return (
      <div
        className='tw-w-full tw-h-10 tw-p-4'
        onClick={() => setIsShowingCrearCuentaModal(true)}
      >
        <div className='tw-cursor-pointer tw-w-full tw-h-full tw-flex tw-justify-start tw-items-center tw-pl-4 tw-hover:bg-background tw-rounded'>
          + {t("cuentasContables.addAccount")} {searchValue ?? ""}
        </div>
      </div>
    )
  }

  const ddRef = useRef<DropdownPrimeReactNewRef>(null)
  const onFilter = (e: DropdownFilterParams) => {
    setSearchValue(e.filter ?? "")
    requestAnimationFrame(() => {
      const input = ddRef.current
        ?.getOverlay?.()
        ?.querySelector<HTMLInputElement>(".p-dropdown-filter")
      if (!input) return
      const keyboardEventRightArrow = new KeyboardEvent("keydown", {
        key: "ArrowRight",
        code: "ArrowRight",
        bubbles: true,
      })
      input.dispatchEvent(keyboardEventRightArrow)
    })
  }

  const filtered = useMemo(() => {
    const query = searchValue.trim().toLowerCase()
    if (!query) return options
    return options.filter((o) => o.label?.toLowerCase().includes(query))
  }, [options, searchValue])

  return (
    <>
      <DropdownPrimeReactNew
        ref={ddRef}
        placeholder={`${t(
          "nsAssets.modalCreate.accountingAccountCounterpartPlaceholder"
        )}`}
        panelClassName='!tw-w-180 !tw-text-sm'
        scrollHeight='350px'
        emptyFilterMessage={`${t("nsAssets.modalCreate.noAccountingAccounts")}`}
        emptyMessage={`${t("nsAssets.modalCreate.noAccountingAccounts")}`}
        disabled={disabled}
        options={filtered}
        className={`tw-w-full tw-rounded tw-h-10 ${className}`}
        id={id}
        showFilterClear
        onChange={onChange}
        loading={loading}
        value={value}
        filter
        pt={{
          input: {
            className: "!tw-text-sm ",
          },
          filterInput: {
            className: "!tw-text-sm",
          },
        }}
        filterInputAutoFocus
        filterPlaceholder={`${t("cuentasContables.searchAccount")}`}
        key={`${id}-cuentacontable`}
        optionLabel='label'
        resetFilterOnHide
        itemTemplate={(option: {
          id: string
          label: string
          numero: string
        }) => (
          <div className='tw-text-ellipsis tw-overflow-hidden'>
            <span
              className={`${option ? "tw-text-text-50" : "tw-text-neutral-50"}`}
            >
              {option
                ? `${option.label}`
                : `${t("cuentasContables.placeholderAccount")}`}
            </span>
          </div>
        )}
        panelFooterTemplate={addCuentaContableTemplate}
        onFilter={onFilter}
        virtualScrollerOptions={{
          itemSize: 40,
        }}
      />
      {withCreateCuenta && (
        <ModalCrearCuentaContable
          visible={isShowingCrearCuentaModal}
          onHide={() => {
            setIsShowingCrearCuentaModal(false)
          }}
          initialData={searchValue}
          nameOnCreate={searchValue ?? ""}
          listaDeCuentasBase={listaDeCuentasBase}
          cuentaPorDefecto={cuentaPorDefecto}
          appNewCuenta={onChange}
          refreshFetchData={refreshFetchData}
        />
      )}
    </>
  )
}

export default DropdownCuentasAsientos
